export interface NavItem {
  to: string;
  label: string;
  permission: string;
  icon: string;
  /** Optionaler Schlüssel für ein Zähler-Badge neben dem Menüpunkt (z.B. offene Anfragen). */
  badgeKey?: "openRequests" | "openOrders";
}

/**
 * Zentrale Navigationsdefinition, von Sidebar (Desktop) und MobileDrawer
 * (Smartphone/Tablet) genutzt. Das System ist ein internes Verwaltungs-
 * programm für den Clubbetrieb - keine Verkaufs- oder Produktkataloge,
 * daher ausschließlich diese Kernmodule.
 *
 * Reihenfolge: "Dashboard" steht IMMER an erster Stelle - bewusst NICHT Teil
 * der alphabetischen Sortierung (deutsche Sortierordnung), die ausschließlich
 * für die Einträge dazwischen gilt. Sidebar.tsx/MobileDrawer.tsx fügen
 * zusätzlich optischen Abstand nach "Dashboard" ein (anhand von
 * item.to === "/" erkannt, 0.9rem statt der sonst üblichen 0.75rem - 20%
 * mehr, damit "Dashboard" klar abgesetzt ist). "Einstellungen" (SETTINGS_ITEM)
 * ist bewusst NICHT Teil von NAV_ITEMS: es muss der WIRKLICH letzte Eintrag
 * im gesamten Menü sein, also unterhalb der hartkodierten Blöcke
 * "Lagersystem"/"Kassensystem"/"Sanktionssystem"/"Route"/"Das Ende"/
 * "Passwort ändern", die sonst nach einem in NAV_ITEMS enthaltenen
 * "Einstellungen" gerendert worden wären. "Passwort ändern" ist bewusst der
 * WIRKLICH letzte Reiter direkt vor "Einstellungen" (bei jedem Benutzer,
 * unabhängig von Rolle/Berechtigungen) - auch der Abstand dorthin ist bewusst
 * 20% größer (0.9rem statt 0.75rem). Sidebar.tsx/MobileDrawer.tsx rendern
 * SETTINGS_ITEM daher separat, ganz am Ende - seit Migration 0046 bewusst
 * OHNE hasPermission()-Prüfung (für JEDEN eingeloggten Benutzer immer
 * sicht- und erreichbar, siehe Settings.tsx "Meine Reiter"), das Feld
 * "permission" an SETTINGS_ITEM steuert dort weiterhin ausschließlich, ob
 * innerhalb der Seite der Bereich "Allgemein" (globale Systemeinstellungen)
 * angezeigt wird. "Einstellungen" ist außerdem als EINZIGER Reiter NICHT
 * über die persönliche Präferenz "Reiter ausblenden" ausblendbar. "Das Ende"
 * und "Lagersystem", "Kassensystem", "Sanktionssystem", "Route" und
 * "Passwort ändern" sind ebenfalls kein Teil von NAV_ITEMS - sie werden in
 * Sidebar.tsx/MobileDrawer.tsx als eigene, hartkodierte Einträge gerendert
 * (siehe dort; "Das Ende" ausschließlich für den Systemadministrator
 * sichtbar, "Lagersystem"/"Kassensystem"/"Sanktionssystem"/"Route" über die
 * eigenen Berechtigungen "discord_sync.view"/"kassensystem.view"/
 * "sanctionssystem.view"/"route.view", siehe Migration 0031/0036/0044/0045 -
 * "route.view" wird dabei ab Werk JEDER regulären Rolle zugewiesen, feinere
 * Rechte innerhalb von "Route" regeln "route.families.manage"/
 * "route.workers.delete" (siehe backend/src/routes/route.ts), "Passwort ändern"
 * für JEDEN eingeloggten Benutzer ohne Berechtigungsprüfung). Alle Reiter
 * AUSSER "Einstellungen" sind zusätzlich über die persönliche Präferenz
 * "Reiter ausblenden" (Migration 0046, users.hidden_nav_items,
 * AuthContext.isNavItemHidden()) individuell pro Benutzer ausblendbar - eine
 * rein kosmetische Anzeige-Einstellung ohne Auswirkung auf Berechtigungen.
 * "Kasse" (Treasury) wurde vollständig entfernt (siehe Migration 0037) -
 * "Kassensystem" ist der eigenständige Nachfolger. "Sanktionen" wurde
 * ebenfalls vollständig entfernt (siehe Migration 0044) - "Sanktionssystem"
 * ist der eigenständige Nachfolger.
 * "Rollen & Rechte" und "Nutzerdaten" sind ebenfalls kein eigener
 * Menüpunkt mehr - beide sind als Reiter in den Menüpunkt "Benutzer"
 * integriert (siehe pages/users/UsersHub.tsx), "Nutzerdaten" dort weiterhin
 * exklusiv für den Systemadministrator sichtbar.
 */
const DASHBOARD_ITEM: NavItem = { to: "/", label: "Dashboard", permission: "dashboard.view", icon: "📊" };

const MIDDLE_ITEMS: NavItem[] = [
  { to: "/personal-inventory", label: "Eigener Lagerbestand", permission: "personal_inventory.view", icon: "🎒" },
  { to: "/members", label: "Mitglieder", permission: "members.view", icon: "🪪" },
  { to: "/family-info", label: "Familieninformationen", permission: "familyinfo.view", icon: "👪" },
  { to: "/requests", label: "Anfragen", permission: "requests.manage", icon: "📥", badgeKey: "openRequests" as const },
  { to: "/users", label: "Benutzer", permission: "users.manage", icon: "👤" },
  { to: "/activity", label: "Benutzeraktivitäten", permission: "activity.view", icon: "📜" },
  { to: "/weekly-dues", label: "Wochenabgabe", permission: "weeklydues.view", icon: "🧾" },
  { to: "/football", label: "Fußball", permission: "football.view", icon: "⚽" },
  { to: "/orders", label: "Bestellungen", permission: "orders.create", icon: "🛒" },
  { to: "/orders-open", label: "Neue Bestellungen", permission: "orders.manage", icon: "📦", badgeKey: "openOrders" as const },
].sort((a, b) => a.label.localeCompare(b.label, "de"));

export const NAV_ITEMS: NavItem[] = [DASHBOARD_ITEM, ...MIDDLE_ITEMS];

/** Immer der letzte Eintrag im gesamten Menü - siehe Kommentar oben. */
export const SETTINGS_ITEM: NavItem = { to: "/settings", label: "Einstellungen", permission: "settings.manage", icon: "⚙️" };
